<template>
    <div class="promotions-slider" v-if="promotions && promotions.length">
        <div class="section-promotions" :class="{'hide': slidesPerView === 3}">
            <div class="section-promotions__header">
                <div class="title">
                    <i class="icon-promotions"></i>
                    <div class="title__text">
                        <h2>{{ $t('front.promotion.slider.title') }}</h2>
                        <p>{{ $t('front.promotion.slider.description') }}</p>
                    </div>
                </div>
                <div class="slider-nav">
                    <router-link :to="seeAllLink">
                        {{ $t('front.promotion.button.see_all') }}
                        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M1 11L6 6L1 1" stroke="#926D20"></path>
                        </svg>
                    </router-link>
                    <v-btn v-on:click="swiperPrev" class="prev" id="#button-prev">
                        <svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 1L2 5L6 9" stroke="white" stroke-width="1.5"/>
                            <path d="M2.5 5H10.5" stroke="white" stroke-width="1.5"/>
                        </svg>
                    </v-btn>
                    <v-btn v-on:click="swiperNext" class="next" id="#button-next">
                        <svg width="11" height="10" viewBox="0 0 11 10" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M5 1L9 5L5 9" stroke="white" stroke-width="1.5"/>
                            <path d="M8.5 5H0.5" stroke="white" stroke-width="1.5"/>
                        </svg>
                    </v-btn>
                </div>
            </div>
            <div class="section-promotions__content" :class="{'count3': slidesPerView === 3}">
                <swiper ref="sliderPromotions" class="section-promotions__slider" :options="sliderSettings">
                    <swiper-slide class="item load" v-for="(item, index) in promotions" :key="index" :data-slide-index="index">
                        <div class="item__box">
                            <div class="item__content"
                                 :data-background="item[isDeviceMobile ? 'mobileImage' : 'desktopImage'][isWebpSupported ? 'webp' : 'jpg']"
                                 :style="{background: item.color}">
                                <h2 v-html="item.title"/>
                                <p v-text="item.description"/>

                                <router-link class="link" @click.native="setPromoIndex(index)"
                                             :to="promotionLink(item)">
                                    {{ $t('front.promotion.button.read_more') }}
                                </router-link>
                            </div>
                        </div>
                    </swiper-slide>
                </swiper>
            </div>
        </div>

        <v-dialog content-class="v-dialog-promotion" v-model="$store.state.dialogs.promotion" max-width="780px"
                  :persistent="isDialogPersistent" v-if="$store.state.dialogs.promotion">
            <promotions-popups @close="closeDialog"
                               :item="dialogItem"
                               :index="promoIndex"
                               :creative="promoCreative"
                               :position="promoPosition"/>
        </v-dialog>
    </div>
</template>

<script>
import {fetchSinglePromotion} from "../../../../../../../src/api/index";
import {showPromotionContentOnPromotionsPage} from "../../../../../../../config/promotion";
import {Swiper, SwiperSlide} from "vue-awesome-swiper";
import DialogSettings from "../../../../../../../src/mixins/dialogSettings";
import apiData from "../../../../../../../src/mixins/apiData";
import {dialogsHashRoutes} from "../../../../../../../config/url";
import eventsInterface from "../../../../../../../src/interfaces/eventsInterface";

export default {
    name: "PromotionSlider",

    props: ['slidesPerView'],

    mixins: [DialogSettings, apiData],

    components: {
        Swiper,
        SwiperSlide,
    },

    data: function () {
        return {
            dialog: false,
            dialogItem: undefined,
            promoIndex: undefined,
            promoCreative: 'Slider',
            promoPosition: this.slidesPerView === 3 ? 'Top' : 'Bottom',
            updateTimeout: undefined,
            imagesLoaded: false,
        }
    },

    serverPrefetch() {
        return this.fetchData();
    },

    mounted() {
        this.fetchData().then(() => this.swiper && setTimeout(() => {
            this.loadCurrentImages();
            this.swiper.on('beforeTransitionStart', this.loadCurrentImages);

            this.trackShowPromotionsEvent();

            this.swiper.on('slideChange', this.trackShowPromotionsEvent);
        }, 100));

        window.addEventListener('resize', this.setUpdateTimeout, false);
    },

    beforeDestroy() {
        window.removeEventListener('resize', this.setUpdateTimeout, false);
        this.clearUpdateTimeout();
    },

    watch: {
        '$route.hash': {
            handler: function (hash) {
                if (hash) {
                    !dialogsHashRoutes.includes(hash) && this.showPromotion(hash.slice(1));
                } else {
                    this.closeDialog();
                }
            },
            deep: true,
            immediate: true
        },

        promotionDialog(isOpen) {
            if (!isOpen && this.$route.hash) {
                this.removeHashFromRoute();
            }
        },

        user(user, oldUser){
            if (user && !oldUser || !user && oldUser){
                this.fetchData(true).then(() => setTimeout(() => this.loadCurrentImages(), 1000));
            }
        },
    },

    computed: {
        isDeviceMobile() {
            return this.$store.state.isDeviceMobile;
        },

        isWebpSupported() {
            return this.$store.state.isWebpSupported;
        },

        promotionDialog() {
            return this.$store.state.dialogs.promotion;
        },

        locale() {
            return this.$store.state.locale;
        },

        sliderSettings() {
            return {
                init: true,
                loop: true,
                slidesPerView: this.slidesPerView || 1,
                direction: 'horizontal',
                spaceBetween: 0,
                speed: 500,
                threshold: 5,
                watchSlidesVisibility: true,
                updateOnWindowResize: true,
                watchSlidesProgress: true,
                centerInsufficientSlides: true,
                slideToClickedSlide: false,
                navigation: {
                    nextEl: '#button-next',
                    prevEl: '#button-prev',
                },
                breakpoints: {
                    768: {
                        slidesPerView: this.slidesPerView || 2,
                        loop: true,
                    },
                },
            };
        },

        user() {
            return this.$store.state.user;
        },

        currency() {
            return this.user && this.user.currency || this.$store.state.guestCurrency;
        },

        country() {
            return this.user && this.user.country || this.$store.state.guestCountry;
        },

        swiper() {
            return this.$refs.sliderPromotions && this.$refs.sliderPromotions.$swiper;
        },

        promotions() {
            return this.$store.state.sliderPromotionsData && this.$store.state.sliderPromotionsData.promotions;
        },

        seeAllLink() {
            let languageUrlPrefix = this.$store.getters.getLanguageUrlPrefix();

            return `${languageUrlPrefix}/promociones`;
        },
    },

    methods: {
        fetchData(mustReload = false) {
            return this.$store.dispatch('FETCH_SLIDER_PROMOTIONS', this.createApiData({
                count: null,
                currency: this.currency,
                country: this.country,
                mustReload: mustReload,
            }));
        },

        setUpdateTimeout() {
            if (this.updateTimeout) {
                this.clearUpdateTimeout();
            }

            this.updateTimeout = setTimeout(this.updateSlider, 400);
        },

        clearUpdateTimeout() {
            clearTimeout(this.updateTimeout);
            this.updateTimeout = null;
        },

        updateSlider() {
            if (this.swiper) {
                this.swiper.update();
                setTimeout(() => this.loadCurrentImages(), 100);
            }
        },

        swiperNext() {
            this.swiper.slideNext()
        },

        swiperPrev() {
            this.swiper.slidePrev()
        },

        promotionLink(item) {
            if (showPromotionContentOnPromotionsPage) {
                return item.pageUrl;
            } else {
                return `${this.$route.path}#${item.slug}` || '';
            }
        },

        showPromotion(slug) {
            fetchSinglePromotion(this.createApiData({
                slug: slug,
            }))
                .then(promotion => {
                    if (promotion) {
                        this.dialogItem = promotion;
                        this.$store.commit('OPEN_PROMOTION_DIALOG');
                    }
                })
                .catch();
        },

        setPromoIndex(index) {
            this.promoIndex = index;
        },

        closeDialog() {
            this.$store.commit('CLOSE_PROMOTION_DIALOG');
        },

        removeHashFromRoute() {
            let redirectRoute = this.$router.resolve({hash: ''});
            this.$router.replace(decodeURIComponent(redirectRoute.resolved.fullPath));
        },

        trackShowPromotionsEvent() {
            setTimeout( () => void require('../../../../../../../src/util/analytics/index').trackEvent(eventsInterface.showPromotions, this, {
                promotions: this.getVisiblePromotions(),
                creative: this.promoCreative,
                position: this.promoPosition,
                user: this.user,
            }), 500);
        },

        getVisiblePromotions() {
            return Array.from(this.$refs.sliderPromotions.$el.getElementsByClassName('swiper-slide-visible')).map(slide => this.promotions[slide.dataset.slideIndex]);
        },

        loadCurrentImages() {
            let swiper = this.$refs.sliderPromotions && this.$refs.sliderPromotions.$swiper;
            
            if (swiper) {
                for (let i = 0; i < swiper.slides.length; i++) {
                    let slide = swiper.slides[i];

                    if (slide.classList.contains('swiper-slide-visible')) {
                        let slideBackgroundElement = slide.firstChild.firstChild;

                        slideBackgroundElement.style.backgroundImage = `url('${slideBackgroundElement.dataset.background}')`;
                    }
                }
            }
        },
    },
}
</script>


<style lang="sass">
@import "sass/components/pages/parts/itemSets/PromotionSlider"
</style>
